// telemetry/log-serializer.js
// Converts raw console-style arguments into a structured { body, attributes } pair
// suitable for OTel log records that are readable in Sentry.
//
// Design rules:
//  - body  = short, human-readable message only (first string or Error.message)
//  - attributes = structured, filterable fields extracted from objects / Errors
//  - sensitive keys are redacted before anything reaches the collector
//  - oversized values are truncated rather than dropped

export const MAX_BODY_LENGTH = 2000
export const MAX_ATTR_VALUE_LENGTH = 512
export const MAX_STACK_LENGTH = 800

// ------------------------------------------------------------------
// Sensitive key detection
// A key is sensitive if it matches any entry in the set, or contains
// known secret substrings (token, secret, password, auth, cookie).
// ------------------------------------------------------------------
const SENSITIVE_EXACT = new Set([
  'authorization',
  'token',
  'access_token',
  'refresh_token',
  'id_token',
  'password',
  'password_reset_url',
  'login_url',
  'cookie',
  'set-cookie',
  'user.email',
  'email',
  'db.query.text',
  'db_query_text',
])

const SENSITIVE_SUBSTRINGS = ['token', 'secret', 'password', 'auth', 'cookie', 'credential']

export const isSensitiveKey = (key) => {
  const normalized = key.toLowerCase().replace(/-/g, '_')
  if (SENSITIVE_EXACT.has(normalized)) return true
  return SENSITIVE_SUBSTRINGS.some((sub) => normalized.includes(sub))
}

// ------------------------------------------------------------------
// Allowlist of object fields that are safe to promote into attributes
// ------------------------------------------------------------------
const PROMOTED_FIELDS = new Set([
  'module',
  'action',
  'feature',
  'request_id',
  'http.request_id',
  'chaos.request_id',
  'error.type',
  'error.message',
  'log.logger',
  'log.module',
  'job.name',
  'hasura.action.name',
  'org',
  'gameKey',
  'game',
  'scenario',
])

// ------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------
const truncate = (str, max = MAX_ATTR_VALUE_LENGTH) => {
  if (typeof str !== 'string') return str
  return str.length > max ? str.slice(0, max) + '…' : str
}

// Regex for common secret patterns embedded inside string bodies
const SECRET_PATTERN = /Bearer\s+[A-Za-z0-9._\-+/]{8,}/g

export const redactSecrets = (str) => {
  if (typeof str !== 'string') return str
  return str.replace(SECRET_PATTERN, 'Bearer [REDACTED]')
}

export const extractErrorAttrs = (err) => ({
  'error.type': (err.constructor?.name !== 'Error' ? err.constructor?.name : null) ?? err.name ?? 'Error',
  'error.message': truncate(err.message ?? ''),
  'error.stack': truncate(err.stack ?? '', MAX_STACK_LENGTH),
})

// Extract only allowlisted safe fields from a plain object into attributes.
export const extractSafeAttrs = (obj) => {
  const attrs = {}
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return attrs
  try {
    for (const key of Object.keys(obj)) {
      if (!PROMOTED_FIELDS.has(key)) continue
      const value = obj[key]
      if (value === null || value === undefined) continue
      if (isSensitiveKey(key)) {
        attrs[key] = '[REDACTED]'
      } else if (typeof value === 'string') {
        attrs[key] = truncate(value)
      } else if (typeof value === 'number' || typeof value === 'boolean') {
        attrs[key] = value
      } else {
        attrs[key] = truncate(String(value))
      }
    }
  } catch {
    // never crash
  }
  return attrs
}

// Produce a short human-readable summary of a plain object for use as body when
// no string message is available.
const summarizeObject = (obj) => {
  try {
    const keys = Object.keys(obj)
    if (keys.length === 0) return '[empty object]'
    const visible = keys.slice(0, 5).join(', ')
    return `{${visible}${keys.length > 5 ? ', …' : ''}}`
  } catch {
    return '[object]'
  }
}

// ------------------------------------------------------------------
// Main serializer
// ------------------------------------------------------------------
/**
 * Serialize console-style variadic arguments into { body, attributes }.
 *
 * Rules:
 *  1. First string arg (or Error.message) becomes body.
 *  2. Error instances → extract error.type / error.message / error.stack attrs.
 *  3. Plain objects → promote allowlisted safe fields into attributes.
 *  4. Additional string args beyond the first → log.arg.N attributes.
 *  5. body is redacted for obvious secret patterns and truncated.
 *
 * @param {any[]} args
 * @returns {{ body: string, attributes: object }}
 */
export const serializeLogArgs = (args) => {
  if (!Array.isArray(args) || args.length === 0) return { body: '', attributes: {} }

  const attributes = {}
  let body = ''
  let bodySet = false

  for (let i = 0; i < args.length; i++) {
    const arg = args[i]

    if (arg instanceof Error) {
      Object.assign(attributes, extractErrorAttrs(arg))
      if (!bodySet) {
        body = truncate(arg.message || '[Error]', MAX_BODY_LENGTH)
        bodySet = true
      }
    } else if (typeof arg === 'string') {
      if (!bodySet) {
        body = truncate(redactSecrets(arg), MAX_BODY_LENGTH)
        bodySet = true
      } else {
        attributes[`log.arg.${i}`] = truncate(redactSecrets(arg))
      }
    } else if (arg !== null && arg !== undefined && typeof arg === 'object') {
      Object.assign(attributes, extractSafeAttrs(arg))
      if (!bodySet) {
        body = summarizeObject(arg)
        bodySet = true
      }
    } else if (arg !== null && arg !== undefined) {
      if (!bodySet) {
        body = truncate(String(arg), MAX_BODY_LENGTH)
        bodySet = true
      } else {
        attributes[`log.arg.${i}`] = truncate(String(arg))
      }
    }
  }

  return { body: body || '', attributes }
}
